import React, { useState, useEffect, useRef } from 'react';
import { Calendar, X, Save, AlertCircle, ImagePlus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { createTransactionPeriodique, updateTransactionPeriodique, TransactionPeriodique, TransactionPeriodiqueWithDetails } from '../lib/services/PeriodicTransactionService';
import { getCategories, Categorie } from '../lib/services/CategoryService';
import { getComptes, Compte } from '../lib/services/AccountService';
import {
  uploadTransactionAttachment,
  removeTransactionAttachmentFile,
  getTransactionAttachmentSignedUrl,
} from '../lib/services/transactionAttachmentService';
import { FREQUENCIES, WEEKDAYS, MONTHS, FREQUENCY_LABELS } from '../config/constants';
import { cleanNumericInput, parseNumericValue, isValidNumericValue } from '../lib/utils/numberUtils';

interface AddPeriodicTransactionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  transactionToEdit?: TransactionPeriodiqueWithDetails | null;
}

// Utilisation des constantes centralisées
const JOURS_SEMAINE = WEEKDAYS;

const AddPeriodicTransactionForm: React.FC<AddPeriodicTransactionFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  transactionToEdit
}) => {
  // États pour les champs du formulaire
  const [description, setDescription] = useState('');
  const [montant, setMontant] = useState('');
  const [type, setType] = useState<'depense' | 'revenu'>('depense');
  const [categorieId, setCategorieId] = useState<string | null>(null);
  const [compteId, setCompteId] = useState<string | null>(null);
  const [dateDebut, setDateDebut] = useState(new Date());
  const [frequence, setFrequence] = useState('mensuelle');
  const [jourExecution, setJourExecution] = useState<number | null>(new Date().getDate());
  const [moisExecution, setMoisExecution] = useState<number | null>(new Date().getMonth());
  const [jourSemaine, setJourSemaine] = useState<number | null>(null);
  const [dateFin, setDateFin] = useState<Date | null>(null);
  const [nombreExecutionsMax, setNombreExecutionsMax] = useState<string>('');
  const [hasEndDate, setHasEndDate] = useState(false);
  const [hasMaxExecutions, setHasMaxExecutions] = useState(false);
  const [actif, setActif] = useState(true);
  const [attachmentPath, setAttachmentPath] = useState<string | null>(null);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState<string | null>(null);
  const [pendingIconFile, setPendingIconFile] = useState<File | null>(null);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // États pour les listes de données
  const [categories, setCategories] = useState<Categorie[]>([]);
  const [comptes, setComptes] = useState<Compte[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Categorie[]>([]);
  
  // États pour les modals
  const [showDateDebutPicker, setShowDateDebutPicker] = useState(false);
  const [showDateFinPicker, setShowDateFinPicker] = useState(false);
  const [showFrequenceModal, setShowFrequenceModal] = useState(false);
  const [showJourSemaineModal, setShowJourSemaineModal] = useState(false);
  const [showJourExecutionModal, setShowJourExecutionModal] = useState(false);
  const [showMoisExecutionModal, setShowMoisExecutionModal] = useState(false);
  const [showCategorieModal, setShowCategorieModal] = useState(false);
  const [showCompteModal, setShowCompteModal] = useState(false);
  
  // États pour le chargement et les erreurs
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charger les données au montage
  useEffect(() => {
    loadData();
  }, []);

  // Charger les données de la transaction à éditer
  useEffect(() => {
    if (transactionToEdit && isOpen) {
      setDescription(transactionToEdit.description || '');
      setMontant(transactionToEdit.montant?.toString() || '');
      setType(transactionToEdit.type || 'depense');
      setCategorieId(transactionToEdit.categorie_id || null);
      setCompteId(transactionToEdit.compte_id || null);
      setDateDebut(transactionToEdit.date_debut ? new Date(transactionToEdit.date_debut) : new Date());
      setFrequence(transactionToEdit.frequence || 'mensuelle');
      setJourExecution(transactionToEdit.jour_execution || null);
      setMoisExecution(transactionToEdit.mois_execution || null);
      setJourSemaine(transactionToEdit.jour_semaine || null);
      setDateFin(transactionToEdit.date_fin ? new Date(transactionToEdit.date_fin) : null);
      setNombreExecutionsMax(transactionToEdit.nombre_executions_max?.toString() || '');
      setHasEndDate(!!transactionToEdit.date_fin);
      setHasMaxExecutions(!!transactionToEdit.nombre_executions_max);
      setActif(transactionToEdit.actif ?? true);
      setAttachmentPath(transactionToEdit.attachment_path ?? null);
      setPendingIconFile(null);
    } else if (!transactionToEdit && isOpen) {
      // Réinitialiser le formulaire si on est en mode création
      resetForm();
    }
  }, [transactionToEdit, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (pendingIconFile) {
      const localUrl = URL.createObjectURL(pendingIconFile);
      setAttachmentPreviewUrl(localUrl);
      return () => URL.revokeObjectURL(localUrl);
    }
    const path = attachmentPath;
    if (!path) {
      setAttachmentPreviewUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { url } = await getTransactionAttachmentSignedUrl(path);
      if (!cancelled && url) setAttachmentPreviewUrl(url);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, attachmentPath, pendingIconFile]);

  // Filtrer les catégories selon le type
  useEffect(() => {
    if (categories.length > 0) {
      setFilteredCategories(categories.filter(cat => cat.type === type || !cat.type));
    }
  }, [categories, type]);

  const loadData = async () => {
    try {
      const [categoriesData, comptesData] = await Promise.all([
        getCategories(),
        getComptes()
      ]);

      if (categoriesData.data) {
        setCategories(categoriesData.data);
      }
      if (comptesData.data) {
        setComptes(comptesData.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setError('Erreur lors du chargement des données');
    }
  };

  const commitIconForId = async (periodiqueId: string, file: File, previousPath: string | null) => {
    const { path, error: upErr } = await uploadTransactionAttachment(periodiqueId, file, 'periodiques');
    if (upErr || !path) {
      return { error: upErr || "Échec de l'envoi de l'icône" };
    }
    const { error: updErr } = await updateTransactionPeriodique(periodiqueId, { attachment_path: path });
    if (updErr) {
      await removeTransactionAttachmentFile(path);
      return {
        error:
          updErr && typeof updErr === 'object' && 'message' in updErr
            ? String((updErr as { message: unknown }).message)
            : "L'icône n'a pas pu être liée",
      };
    }
    if (previousPath && previousPath !== path) {
      await removeTransactionAttachmentFile(previousPath);
    }
    return { path, error: null as string | null };
  };

  const handleIconPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    // Création : on garde le fichier jusqu'après insert
    if (!transactionToEdit?.id) {
      setPendingIconFile(file);
      setAttachmentPath(null);
      return;
    }

    setAttachmentBusy(true);
    setError(null);
    try {
      const previous = attachmentPath;
      const { path, error: commitErr } = await commitIconForId(transactionToEdit.id, file, previous);
      if (commitErr || !path) {
        setError(commitErr || "Échec de l'envoi de l'icône");
        return;
      }
      setAttachmentPath(path);
      setPendingIconFile(null);
    } finally {
      setAttachmentBusy(false);
    }
  };

  const handleRemoveIcon = async () => {
    if (pendingIconFile) {
      setPendingIconFile(null);
      setAttachmentPreviewUrl(null);
      return;
    }
    if (!attachmentPath) return;

    // Création sans id encore : rien en storage
    if (!transactionToEdit?.id) {
      setAttachmentPath(null);
      setAttachmentPreviewUrl(null);
      return;
    }

    setAttachmentBusy(true);
    setError(null);
    try {
      const path = attachmentPath;
      const { error: updErr } = await updateTransactionPeriodique(transactionToEdit.id, {
        attachment_path: null,
      });
      if (updErr) {
        setError(
          updErr && typeof updErr === 'object' && 'message' in updErr
            ? String((updErr as { message: unknown }).message)
            : 'Impossible de retirer l’icône',
        );
        return;
      }
      setAttachmentPath(null);
      setAttachmentPreviewUrl(null);
      await removeTransactionAttachmentFile(path);
    } finally {
      setAttachmentBusy(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!description.trim()) {
      setError('La description est obligatoire');
      return;
    }
    
    if (!isValidNumericValue(montant)) {
      setError('Le montant doit être un nombre valide');
      return;
    }
    
    const montantValue = parseNumericValue(montant);
    if (montantValue <= 0) {
      setError('Le montant doit être supérieur à 0');
      return;
    }
    
    if (!compteId) {
      setError('Le compte est obligatoire');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const transactionDataBase: TransactionPeriodique = {
        description: description.trim(),
        montant: montantValue,
        type: type,
        compte_id: compteId,
        date_debut: dateDebut.toISOString().split('T')[0]!,
        frequence: frequence as any,
        actif,
      };

      if (categorieId) {
        transactionDataBase.categorie_id = categorieId;
      }
      if (jourExecution !== null && jourExecution !== undefined) {
        transactionDataBase.jour_execution = jourExecution;
      }
      if (moisExecution !== null && moisExecution !== undefined) {
        transactionDataBase.mois_execution = moisExecution;
      }
      if (jourSemaine !== null && jourSemaine !== undefined) {
        transactionDataBase.jour_semaine = jourSemaine;
      }
      if (dateFin) {
        transactionDataBase.date_fin = dateFin.toISOString().split('T')[0]!;
      }
      if (nombreExecutionsMax) {
        transactionDataBase.nombre_executions_max = parseInt(nombreExecutionsMax);
      }

      const transactionData: TransactionPeriodique = transactionDataBase;

      if (transactionToEdit?.id) {
        // Mode édition (l'icône est déjà gérée à la sélection / retrait)
        const { error } = await updateTransactionPeriodique(transactionToEdit.id, transactionData);
        if (error) {
          console.error('Erreur lors de la mise à jour:', error);
          setError((error as any)?.message || 'Erreur lors de la mise à jour de la transaction périodique');
        } else {
          resetForm();
          onSuccess();
          onClose();
        }
      } else {
        // Mode création
        const { data, error } = await createTransactionPeriodique(transactionData);
        if (error) {
          console.error('Erreur lors de la création:', error);
          setError((error as any)?.message || 'Erreur lors de la création de la transaction périodique');
        } else {
          let iconWarning: string | null = null;
          if (data?.id && pendingIconFile) {
            const { error: iconErr } = await commitIconForId(data.id, pendingIconFile, null);
            if (iconErr) iconWarning = iconErr;
          }
          resetForm();
          onSuccess();
          onClose();
          if (iconWarning) {
            console.warn('Icône périodique:', iconWarning);
          }
        }
      }
    } catch (error) {
      console.error('Exception lors de la création:', error);
      setError('Une erreur est survenue lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDescription('');
    setMontant('');
    setType('depense');
    setCategorieId(null);
    setCompteId(null);
    setDateDebut(new Date());
    setFrequence('mensuelle');
    setJourExecution(new Date().getDate());
    setMoisExecution(new Date().getMonth());
    setJourSemaine(null);
    setDateFin(null);
    setNombreExecutionsMax('');
    setHasEndDate(false);
    setHasMaxExecutions(false);
    setActif(true);
    setAttachmentPath(null);
    setAttachmentPreviewUrl(null);
    setPendingIconFile(null);
    setError(null);
  };

  const getSelectedCategorieName = () => {
    if (!categorieId) return 'Sélectionner une catégorie';
    const categorie = categories.find(c => c.id === categorieId);
    return categorie ? categorie.nom : 'Catégorie inconnue';
  };

  const getSelectedCompteName = () => {
    if (!compteId) return 'Sélectionner un compte';
    const compte = comptes.find(c => c.id === compteId);
    return compte ? compte.nom : 'Compte inconnu';
  };


  const getFrequenceLabel = () => {
    return FREQUENCY_LABELS[frequence as keyof typeof FREQUENCY_LABELS] || frequence;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 border border-dark-700 rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between shrink-0 px-6 pt-6 pb-4">
          <h2 className="text-xl font-bold text-white">
            {transactionToEdit ? 'Modifier la transaction périodique' : 'Nouvelle transaction périodique'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors"
            aria-label="Fermer"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="shrink-0 mx-6 mb-2 p-3 bg-red-500 bg-opacity-20 border border-red-500 rounded-lg flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-6 space-y-4">
          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-dark-400 mb-2">Description *</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
              placeholder="Ex: Loyer, Salaire, etc."
            />
          </div>

          {/* Montant */}
          <div>
            <label className="block text-sm font-medium text-dark-400 mb-2">Montant *</label>
            <input
              type="text"
              inputMode="decimal"
              value={montant}
              onChange={(e) => {
                const cleaned = cleanNumericInput(e.target.value);
                setMontant(cleaned);
              }}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
              placeholder="0.00"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-dark-400 mb-2">Type *</label>
            <div className="flex space-x-2">
              {['depense', 'revenu'].map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t as any)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    type === t
                      ? 'bg-primary-500 text-white'
                      : 'bg-dark-800 text-dark-400 hover:bg-dark-700'
                  }`}
                >
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Catégorie */}
          <div>
            <label className="block text-sm font-medium text-dark-400 mb-2">Catégorie</label>
            <button
              onClick={() => setShowCategorieModal(true)}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-left hover:bg-dark-700 transition-colors"
            >
              {getSelectedCategorieName()}
            </button>
          </div>

          {/* Compte */}
          <div>
            <label className="block text-sm font-medium text-dark-400 mb-2">Compte *</label>
            <button
              onClick={() => setShowCompteModal(true)}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-left hover:bg-dark-700 transition-colors"
            >
              {getSelectedCompteName()}
            </button>
          </div>


          {/* Date de début */}
          <div>
            <label className="block text-sm font-medium text-dark-400 mb-2">Date de début *</label>
            <button
              onClick={() => setShowDateDebutPicker(true)}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-left hover:bg-dark-700 transition-colors flex items-center space-x-2"
            >
              <Calendar className="w-4 h-4" />
              <span>{format(dateDebut, 'dd/MM/yyyy', { locale: fr })}</span>
            </button>
          </div>

          {/* Fréquence */}
          <div>
            <label className="block text-sm font-medium text-dark-400 mb-2">Fréquence *</label>
            <button
              onClick={() => setShowFrequenceModal(true)}
              className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-left hover:bg-dark-700 transition-colors"
            >
              {getFrequenceLabel()}
            </button>
          </div>

          {/* Paramètres spécifiques à la fréquence */}
          {frequence === 'hebdomadaire' && (
            <div>
              <label className="block text-sm font-medium text-dark-400 mb-2">Jour de la semaine</label>
              <button
                onClick={() => setShowJourSemaineModal(true)}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-left hover:bg-dark-700 transition-colors"
              >
                {jourSemaine !== null ? JOURS_SEMAINE.find(j => j.value === jourSemaine)?.label : 'Sélectionner un jour'}
              </button>
            </div>
          )}

          {(frequence === 'mensuelle' || frequence === 'trimestrielle') && (
            <div>
              <label className="block text-sm font-medium text-dark-400 mb-2">Jour du mois</label>
              <button
                onClick={() => setShowJourExecutionModal(true)}
                className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-left hover:bg-dark-700 transition-colors"
              >
                {jourExecution || 'Sélectionner un jour'}
              </button>
            </div>
          )}

          {frequence === 'annuelle' && (
            <>
              <div>
                <label className="block text-sm font-medium text-dark-400 mb-2">Mois</label>
                <button
                  onClick={() => setShowMoisExecutionModal(true)}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-left hover:bg-dark-700 transition-colors"
                >
                  {moisExecution !== null ? MONTHS.find(m => m.value === moisExecution)?.label : 'Sélectionner un mois'}
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-400 mb-2">Jour du mois</label>
                <button
                  onClick={() => setShowJourExecutionModal(true)}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-left hover:bg-dark-700 transition-colors"
                >
                  {jourExecution || 'Sélectionner un jour'}
                </button>
              </div>
            </>
          )}

          {/* Options avancées */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-white">Options avancées</h3>
            
            {/* Date de fin */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="hasEndDate"
                checked={hasEndDate}
                onChange={(e) => setHasEndDate(e.target.checked)}
                className="w-4 h-4 text-primary-500 bg-dark-800 border-dark-700 rounded focus:ring-primary-500"
              />
              <label htmlFor="hasEndDate" className="text-white">Date de fin</label>
            </div>
            
            {hasEndDate && (
              <div>
                <button
                  onClick={() => setShowDateFinPicker(true)}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white text-left hover:bg-dark-700 transition-colors flex items-center space-x-2"
                >
                  <Calendar className="w-4 h-4" />
                  <span>{dateFin ? format(dateFin, 'dd/MM/yyyy', { locale: fr }) : 'Sélectionner une date'}</span>
                </button>
              </div>
            )}

            {/* Nombre maximum d'exécutions */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="hasMaxExecutions"
                checked={hasMaxExecutions}
                onChange={(e) => setHasMaxExecutions(e.target.checked)}
                className="w-4 h-4 text-primary-500 bg-dark-800 border-dark-700 rounded focus:ring-primary-500"
              />
              <label htmlFor="hasMaxExecutions" className="text-white">Nombre maximum d'exécutions</label>
            </div>
            
            {hasMaxExecutions && (
              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={nombreExecutionsMax}
                  onChange={(e) => {
                    // Nettoyer pour n'accepter que les entiers
                    const cleaned = e.target.value.replace(/[^0-9]/g, '');
                    const finalValue = cleanNumericInput(cleaned);
                    setNombreExecutionsMax(finalValue);
                  }}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  placeholder="Nombre d'exécutions"
                />
              </div>
            )}

            {/* Actif */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="actif"
                checked={actif}
                onChange={(e) => setActif(e.target.checked)}
                className="w-4 h-4 text-primary-500 bg-dark-800 border-dark-700 rounded focus:ring-primary-500"
              />
              <label htmlFor="actif" className="text-white">Transaction active</label>
            </div>
          </div>

          {/* Icône (optionnel) */}
          <div className="border border-dark-600 rounded-lg p-3 space-y-2 mb-2">
            <label className="block text-sm font-medium text-white">Icône (optionnel)</label>
            <p className="text-[11px] text-dark-400">
              Logo ou image affichée en rond dans la liste (ex. Free, AXA…).
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleIconPick}
              disabled={attachmentBusy || loading}
            />
            {attachmentPreviewUrl && (
              <div className="w-16 h-16 rounded-full overflow-hidden border border-dark-600 bg-dark-900">
                <img
                  src={attachmentPreviewUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={attachmentBusy || loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-dark-700 text-white rounded-md hover:bg-dark-600 disabled:opacity-50"
              >
                <ImagePlus className="w-4 h-4" />
                {attachmentPath || pendingIconFile ? "Remplacer l'icône" : 'Choisir une image'}
              </button>
              {(attachmentPath || pendingIconFile) && (
                <button
                  type="button"
                  onClick={handleRemoveIcon}
                  disabled={attachmentBusy || loading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-500/15 text-red-300 rounded-md hover:bg-red-500/25 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Retirer
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Boutons d'action — toujours visibles */}
        <div className="shrink-0 flex justify-end space-x-3 px-6 pt-4 pb-6 border-t border-dark-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
            disabled={loading || attachmentBusy}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || attachmentBusy}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg transition-colors"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{loading ? (transactionToEdit ? 'Mise à jour...' : 'Création...') : (transactionToEdit ? 'Modifier' : 'Créer')}</span>
          </button>
        </div>

        {/* Modals */}
        {/* Modal de sélection de catégorie */}
        {showCategorieModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 w-full max-w-md mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-4">Sélectionner une catégorie</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                <button
                  onClick={() => {
                    setCategorieId(null);
                    setShowCategorieModal(false);
                  }}
                  className="w-full px-3 py-2 text-left text-white hover:bg-dark-700 rounded-lg transition-colors"
                >
                  Aucune catégorie
                </button>
                {filteredCategories.map((categorie) => (
                  <button
                    key={categorie.id}
                    onClick={() => {
                      setCategorieId(categorie.id!);
                      setShowCategorieModal(false);
                    }}
                    className="w-full px-3 py-2 text-left text-white hover:bg-dark-700 rounded-lg transition-colors"
                  >
                    {categorie.nom}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowCategorieModal(false)}
                className="mt-4 w-full px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        )}

        {/* Modal de sélection de compte */}
        {showCompteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 w-full max-w-md mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-4">Sélectionner un compte</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {comptes.map((compte) => (
                  <button
                    key={compte.id}
                    onClick={() => {
                      setCompteId(compte.id!);
                      setShowCompteModal(false);
                    }}
                    className="w-full px-3 py-2 text-left text-white hover:bg-dark-700 rounded-lg transition-colors"
                  >
                    {compte.nom}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowCompteModal(false)}
                className="mt-4 w-full px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        )}


        {/* Modal de sélection de fréquence */}
        {showFrequenceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 w-full max-w-md mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-4">Sélectionner une fréquence</h3>
              <div className="space-y-2">
                {FREQUENCIES.map((freq) => (
                  <button
                    key={freq}
                    onClick={() => {
                      setFrequence(freq);
                      setShowFrequenceModal(false);
                    }}
                    className="w-full px-3 py-2 text-left text-white hover:bg-dark-700 rounded-lg transition-colors"
                  >
                    {FREQUENCY_LABELS[freq as keyof typeof FREQUENCY_LABELS]}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowFrequenceModal(false)}
                className="mt-4 w-full px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        )}

        {/* Modal de sélection de jour de la semaine */}
        {showJourSemaineModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 w-full max-w-md mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-4">Sélectionner un jour de la semaine</h3>
              <div className="space-y-2">
                {JOURS_SEMAINE.map((jour) => (
                  <button
                    key={jour.value}
                    onClick={() => {
                      setJourSemaine(jour.value);
                      setShowJourSemaineModal(false);
                    }}
                    className="w-full px-3 py-2 text-left text-white hover:bg-dark-700 rounded-lg transition-colors"
                  >
                    {jour.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowJourSemaineModal(false)}
                className="mt-4 w-full px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        )}

        {/* Modal de sélection de jour d'exécution */}
        {showJourExecutionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 w-full max-w-md mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-4">Sélectionner un jour du mois</h3>
              <div className="grid grid-cols-7 gap-2 max-h-64 overflow-y-auto">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((jour) => (
                  <button
                    key={jour}
                    onClick={() => {
                      setJourExecution(jour);
                      setShowJourExecutionModal(false);
                    }}
                    className="px-3 py-2 text-white hover:bg-dark-700 rounded-lg transition-colors"
                  >
                    {jour}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowJourExecutionModal(false)}
                className="mt-4 w-full px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        )}

        {/* Modal de sélection de mois */}
        {showMoisExecutionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 w-full max-w-md mx-4 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-4">Sélectionner un mois</h3>
              <div className="space-y-2">
                {MONTHS.map((mois) => (
                  <button
                    key={mois.value}
                    onClick={() => {
                      setMoisExecution(mois.value);
                      setShowMoisExecutionModal(false);
                    }}
                    className="w-full px-3 py-2 text-left text-white hover:bg-dark-700 rounded-lg transition-colors"
                  >
                    {mois.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowMoisExecutionModal(false)}
                className="mt-4 w-full px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        )}

        {/* Date pickers */}
        {showDateDebutPicker && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-4">Sélectionner la date de début</h3>
              <input
                type="date"
                value={dateDebut.toISOString().split('T')[0]}
                onChange={(e) => {
                  setDateDebut(new Date(e.target.value));
                  setShowDateDebutPicker(false);
                }}
                className="px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
              />
              <button
                onClick={() => setShowDateDebutPicker(false)}
                className="mt-4 w-full px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        )}

        {showDateFinPicker && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-dark-800 border border-dark-700 rounded-lg p-4 shadow-xl">
              <h3 className="text-lg font-bold text-white mb-4">Sélectionner la date de fin</h3>
              <input
                type="date"
                value={dateFin ? dateFin.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  setDateFin(new Date(e.target.value));
                  setShowDateFinPicker(false);
                }}
                className="px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
              />
              <button
                onClick={() => setShowDateFinPicker(false)}
                className="mt-4 w-full px-4 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddPeriodicTransactionForm; 